import { useCallback } from 'react'
import { exportToCsv, formatDate } from 'src/utils'

export interface ExportConfig {
  columns: Record<string, string>
  fileName: string
  dataTransform?: (data: any[]) => any[]
}

export const useExport = (config: ExportConfig) => {
  const exportData = useCallback(
    (dataToExport: any[]) => {
      if (!dataToExport?.length) {
        console.warn('Nenhum dado para exportar')
        return
      }

      const transformedData = config.dataTransform ? config.dataTransform(dataToExport) : dataToExport

      exportToCsv(transformedData, config.columns, config.fileName)
    },
    [config]
  )

  return { exportData }
}

// Hook específico para Deaths and Diseases
export const useDeathsAndDiseasesExport = () => {
  const config: ExportConfig = {
    columns: {
      idAnimal: 'ID ANIMAL',
      data: 'DATA',
      animal: 'ANIMAL',
      ocorrencia: 'OCORRÊNCIA',
      causa: 'CAUSA'
    },
    fileName: 'morte_e_doenças',
    dataTransform: (data: any[]) =>
      data.map((item) => ({
        ...item,
        data: formatDate(item.data),
        animal: item.animal?.nome
      }))
  }

  return useExport(config)
}

// Hook específico para Genetic Evaluation
export const useGeneticEvaluationExport = () => {
  const config: ExportConfig = {
    columns: {
      rgn: 'RGN',
      raca: 'Raça',
      nascimento: 'Nascimento',
      sexo: 'Sexo',
      ige: 'IGE',
      acc_ige: 'ACC_IGE',
      top_ige: 'TOP_IGE'
    },
    fileName: 'Avaliação Genética'
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_'),
    dataTransform: (data: any[]) =>
      data.map((item) => ({
        ...item,
        rgn: item.animal.rgn,
        raça: item.animal.raca,
        sexo: item.animal.sexo,
        nascimento: formatDate(item.animal.dataNascimento || '')
      }))
  }

  return useExport(config)
}

// Hook específico para Genotyping
export const useGenotypingExport = () => {
  const config: ExportConfig = {
    columns: {
      codigoBarras: 'Código de Barras',
      serie: 'Série',
      rgn: 'RGN',
      rgd: 'RGD',
      nascimento: 'Nascimento',
      nome: 'Nome',
      sexo: 'Sexo',
      cat: 'CAT',
      var: 'VAR' // eslint-disable-line
    },
    fileName: 'genotipagem',
    dataTransform: (data: any[]) =>
      data.map((item) => ({
        ...item,
        ...item.animal,
        sexo: item.animal.sexo === 'M' ? 'Macho' : 'Fêmea',
        nascimento: formatDate(item?.animal?.dataNascimento || '')
      }))
  }

  return useExport(config)
}

// Hook específico para Pregnancy Diagnosis
export const usePregnancyDiagnosisExport = () => {
  const config: ExportConfig = {
    columns: {
      idAnimal: 'ID ANIMAL',
      dataCobertura: 'DATA COBERTURA',
      vaca: 'VACA',
      touro: 'TOURO',
      diagnostico: 'DIAGNÓTICO',
      previsaoParto: 'PREV. PARTO',
      dataParto: 'DATA PARTO',
      status: 'STATUS'
    },
    fileName: 'diagnostico_gestacao',
    dataTransform: (data: any[]) =>
      data.map((item) => ({
        ...item,
        dataCobertura: formatDate(item.dataCobertura),
        previsaoParto: formatDate(item.previsaoParto),
        dataParto: formatDate(item.dataParto),
        diagnostico: item.historico[0].status,
        status: item.status === 'emProcesso' ? 'em processo' : 'concluido'
      }))
  }

  return useExport(config)
}
